import { apiService } from '@/services/api/apiService';
import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { Key } from 'readline';

interface City {
    id: number | string;
    cityName: string;
    isActive: boolean;
    countryId: number;
    auditTrailId: number | null;
}

interface CityState {
    data: City[];
    loading: boolean;
    error: string | null;
}

const MASTERPORT = import.meta.env.VITE_USERS_PORT;
const MASTERURL = import.meta.env.VITE_API_USERS_URL;

export const fetchCity = createAsyncThunk<City[], void, { rejectValue: string }>(
    'City/fetchCity',
    async (_, { rejectWithValue }) => {
        try {
            const response = await apiService.get<{ data: City[] }>('/get_all_city', MASTERPORT, MASTERURL);
            return response.data;
        } catch (error) {
            return rejectWithValue(error instanceof Error ? error.message : 'An error occurred');
        }
    }
);

export const createCity = createAsyncThunk<City, any, { rejectValue: string }>(
    'City/createCity',
    async (params, { rejectWithValue }) => {
        try {
            const response = await apiService.post<{ data: City }>('create_city', params, MASTERPORT, MASTERURL);
            return response.data;
        } catch (error) {
            return rejectWithValue(error instanceof Error ? error.message : 'An error occurred');
        }
    }
);
export const getByIdCity = createAsyncThunk<
    City[],
    { id: number | string },
    { rejectValue: string }
>(

    'City/getByIdCity',
    async (params, { rejectWithValue }) => {
        try {
            const config = { params };
            const response = await apiService.get<any>('get_by_id_city', MASTERPORT, MASTERURL, config
            );
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.message || 'An error occurred');
        }
    }
);

export const updateCity = createAsyncThunk<City, any, { rejectValue: string }>(
    'City/updateCity',
    async (params, { rejectWithValue }) => {
        try {
            const response = await apiService.put<{ data: City }>('update_city', params, MASTERPORT, MASTERURL);
            return response.data;
        } catch (error) {
            return rejectWithValue(error instanceof Error ? error.message : 'An error occurred');
        }
    }
);

export const softDeleteCity: any = createAsyncThunk<
    number,
    number
>(
    "DeleteCity/softDeleteCity",
    async (id,) => {
        try {
            const response = await apiService.patch(
                `soft_delete_city?id=${id}`,
                {},
                MASTERPORT,
                MASTERURL
            );

            return response



        } catch (error: any) {
            return (
                error?.message || "An error occurred while deleting cuisine type"
            );
        }
    }
);

export const CitySlice = createSlice({
    name: 'City',
    initialState: {
        data: [],
        loading: false,
        error: null,
    } as CityState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(fetchCity.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchCity.fulfilled, (state, action: PayloadAction<City[]>) => {
                state.loading = false;
                state.data = action.payload;
            })
            .addCase(fetchCity.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload ?? 'Unknown error';
            })
            .addCase(createCity.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(createCity.fulfilled, (state, action: PayloadAction<City>) => {
                state.loading = false;
                state.data.push(action.payload);
            })
            .addCase(createCity.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload ?? 'Unknown error';
            })
            .addCase(getByIdCity.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(getByIdCity.fulfilled, (state, action) => {
                state.loading = false;
                state.data = action.payload;
            })
            .addCase(getByIdCity.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload || 'Failed to load cities';
            })
            .addCase(updateCity.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(updateCity.fulfilled, (state, action: PayloadAction<City>) => {
                state.loading = false;
                state.data.push(action.payload);
            })
            .addCase(updateCity.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload ?? 'Unknown error';
            })
    },
});

export default CitySlice.reducer;